import { tryParseBoolean } from '@witkac/utils/tryParseBoolean';
import { RouteConfig } from 'vue-router';

import store from '@/store/store';
import { baseFilters } from '@/utils/baseFilters';
import { tryParseInt } from '@witkac/utils/tryParseInt';

export const clientListRoute: RouteConfig = {
  path: 'clients',
  name: 'clientList',
  component: () => import(/* webpackChunkName: "ClientProfileList" */ '@/views/client/List.vue'),
  beforeEnter: (to, from, next) => {
    store.commit('breadcrumbs/set', [{ text: 'Mieszkańcy', disabled: true }]);
    store
      .dispatch('client/list', baseFilters(to))
      .then(() => next())
      .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać listy mieszkańców' }));
  },
  meta: { toolbarTitle: 'Wyszukaj klienta' }
};

export const clientDetailsRoute: RouteConfig = {
  path: 'clients/:id',
  component: () => import(/* webpackChunkName: "ClientView" */ '@/views/client/ClientView.vue'),
  beforeEnter: (to, from, next) => {
    store.commit('client/setId', to.params.id);
    return store
      .dispatch('client/get')
      .then(res => {
        store.commit('breadcrumbs/set', [
          { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
          { text: res.firstName + ' ' + res.lastName, disabled: true }
        ]);

        next();
      })
      .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
  },
  children: [
    {
      path: '',
      name: 'client',
      component: () => import(/* webpackChunkName: "ClientProfile" */ '@/views/client/Profile.vue'),
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },

    {
      path: 'usage',
      name: 'clientUsage',
      component: () => import(/* webpackChunkName: "BenefitsHistory" */ '@/components/BenefitUsageHistory.vue'),
      beforeEnter: (to, from, next) => {
        return store
          .dispatch('benefitsHistory/list', { ...baseFilters(to), profileId: tryParseInt(to.params.id) })
          .then(() => next())
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać historii użyć karty' }));
      },
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },
    {
      path: 'history',
      name: 'clientHistory',
      component: () => import(/* webpackChunkName: "EntityHistory" */ '@/components/EntityHistory.vue'),
      beforeEnter: (to, from, next) => {
        Promise.all([
          store.dispatch('client/cards/list'),
          store.dispatch('entityHistory/list', {
            ...baseFilters(to),
            entityId: to.params.id,
            tableName: 'Profile',
            advanced: tryParseBoolean(to.query.advancedHistory, false)
          })
        ])
          .then(() => next())
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
      },
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },
    {
      path: 'groups',
      name: 'clientGroups',
      component: () => import(/* webpackChunkName: "ClientGroups" */ '@/views/client/tabs/Groups.vue'),
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },
    {
      path: 'cards',
      name: 'clientCards',
      component: () => import(/* webpackChunkName: "ClientCards" */ '@/views/client/tabs/Cards.vue'),

      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },
    {
      path: 'applications',
      component: () => import(/* webpackChunkName: "ClientApplicationTab" */ '@/views/client/tabs/Applications.vue'),
      children: [
        {
          path: '',
          name: 'clientApplications',
          component: () => import(/* webpackChunkName: "ClientApplications" */ '@/views/client/Applications.vue'),
          beforeEnter: (to, from, next) => {
            store.commit('client/setId', to.params.id);
            store
              .dispatch('client/get')
              .then(client => {
                return store
                  .dispatch(`application/list`, { ...baseFilters(to), profileId: client.id, withInactive: true })
                  .then(res => {
                    store.commit('breadcrumbs/set', [
                      { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
                      { text: client.firstName + ' ' + client.lastName, disabled: true },
                      { text: 'Wnioski', disabled: true }
                    ]);
                    next();
                  });
              })
              .catch(() =>
                store.dispatch('notifyError', {
                  title: 'Nie udało się pobrać danych klienta.'
                })
              );
          },
          meta: {
            toolbarTitle: 'Profil klienta'
          }
        },
        {
          path: ':applicationId',
          name: 'clientApplication',
          component: () => import(/* webpackChunkName: "ClientApplication" */ '@/views/client/Application.vue'),
          beforeEnter: (to, from, next) => {
            store.commit('client/setId', to.params.id);
            store.commit('application/setId', parseInt(to.params.applicationId));
            Promise.all([store.dispatch('client/get'), store.dispatch('application/get')])
              .then(([client, application]) => {
                const isNewApplication = application.type === 'NEW';
                return store
                  .dispatch(
                    'settings/settingFind',
                    isNewApplication ? 'APPLICATION_NEW_GROUPS_FORM' : 'APPLICATION_EXTEND_GROUPS_FORM'
                  )
                  .then(() => ({ client, application }));
              })
              .then(({ client, application }) => {
                store.commit('breadcrumbs/set', [
                  { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
                  { text: client.firstName + ' ' + client.lastName, disabled: true },
                  { text: 'Wnioski', disabled: true },
                  { text: `#${application.id}`, disabled: true }
                ]);

                next();
              })
              .catch(() =>
                store.dispatch('notifyError', {
                  title: 'Nie udało się pobrać danych klienta'
                })
              );
          },
          meta: {
            toolbarTitle: 'Profil klienta'
          }
        }
      ]
    },
    {
      path: 'points',
      name: 'clientPoints',
      component: () => import(/* webpackChunkName: "ClientPoints" */ '@/views/client/tabs/Points.vue'),
      beforeEnter: (to, from, next) => {
        return store
          .dispatch('client/containerPoints/list', {
            profileId: tryParseInt((store.state as any).client.data.profile.id)
          })
          .then(res => next())
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
      },
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    }
  ]
};
